import { Lang } from '../Lang';
import { Card2, ContentCol2, ContentColSize2, ContentRow2, LangTextV2, Select2 } from 'bambooo';
import { fromLonLat } from 'ol/proj';
import { Organization as OrganizationAPI } from '../Api/Organization';
import { Sightings as SightingsApi } from '../Api/Sightings';
import { SightingEnvironment } from '../Api/SightingEnvironment';
import { User as UserAPI } from '../Api/User';
import { BaseMap } from '../Map/BaseMap';
import { AirTemperatureMetricLayer } from '../Map/Layers/AirTemperatureMetricLayer';
import { BathymetryLayer } from '../Map/Layers/BathymetryLayer';
import { ChlorophyllMetricLayer } from '../Map/Layers/ChlorophyllMetricLayer';
import { FishingEffortMetricLayer } from '../Map/Layers/FishingEffortMetricLayer';
import { IdeeEsLayer } from '../Map/Layers/IdeeEsLayer';
import { OceanCurrentsLayer } from '../Map/Layers/OceanCurrentsLayer';
import { OldSightingPointsLayer } from '../Map/Layers/OldSightingPointsLayer';
import { OsmBaseLayer } from '../Map/Layers/OsmBaseLayer';
import { SeaSurfaceTemperatureMetricLayer } from '../Map/Layers/SeaSurfaceTemperatureMetricLayer';
import { WaveHeightMetricLayer } from '../Map/Layers/WaveHeightMetricLayer';
import { CurrentRegionQuiverModal } from '../Map/Widgets/CurrentRegionQuiverModal';
import { MetricCharts } from '../Map/Widgets/MetricCharts';
import { ProvenanceLegend } from '../Map/Widgets/ProvenanceLegend';
import { BasePage } from './BasePage';
/**
 * Ocean & Fishing-effort map page.
 *
 * Renders the sighting set on top of OSM/EMODnet/IDEE base layers,
 * with two interchangeable metric overlays:
 *   - {@link ChlorophyllMetricLayer} — primary productivity proxy
 *     (NOAA CoastWatch ERDDAP, chl-a).
 *   - {@link FishingEffortMetricLayer} — commercial fishing pressure
 *     (Global Fishing Watch, 25 km radius).
 *
 * Both layers are added to the LayerSwitcher; the user opts in by
 * checking the matching entry. The right-side {@link ProvenanceLegend}
 * lists every data source the page draws from, and the bottom row
 * shows {@link MetricCharts} (histogram + scatter + monthly mean) over
 * the loaded sighting set.
 */
export class OceanFishingMap extends BasePage {
    constructor() {
        super(...arguments);
        /**
         * @protected
         */
        this._name = 'ocean_fishing_map';
        /**
         * @protected
         */
        this._map = null;
        /**
         * Metric layers in the order they appear in the LayerSwitcher.
         * The list also feeds the ProvenanceLegend and the per-layer
         * point updates in {@link _reload}, so adding a new metric layer
         * is a single line + accessor.
         * @protected
         */
        this._metricLayers = [];
        /**
         * Historical (pre-app) sightings layer — opt-in via the LayerSwitcher,
         * lazy-loaded on first show. Kept out of {@link _metricLayers} so old
         * rows never feed the metric overlays or the effort-sensitive charts.
         * @protected
         */
        this._oldLayer = null;
        /**
         * Unsubscribe handle for the old-layer visibility listener.
         * @protected
         */
        this._oldLayerUnsub = null;
        /**
         * @protected
         */
        this._legend = null;
        /**
         * @protected
         */
        this._charts = null;
        /**
         * Selected year as a 4-digit string, or '' for "all sightings".
         * @protected
         */
        this._selectedYear = '';
        /**
         * Two stored heights for the toggle button: "big" uses (almost) the
         * full viewport so the operator can pan/zoom comfortably, "small"
         * gives the chart row room to fit on the same screen. Values are
         * computed once during loadContent and re-used by {@link _applyMapHeight}.
         * @protected
         */
        this._mapBigHeight = 600;
        /**
         * @protected
         */
        this._mapSmallHeight = 360;
        /**
         * @protected
         */
        this._mapIsBig = true;
    }
    async unloadContent() {
        if (this._oldLayerUnsub) {
            this._oldLayerUnsub();
            this._oldLayerUnsub = null;
        }
        if (this._map) {
            this._map.disposePopover(true);
            this._map.unload();
            this._map = null;
        }
        jQuery(document).off('click.cmemsRegion');
        this._metricLayers = [];
        this._oldLayer = null;
        this._legend = null;
        this._charts = null;
        this._selectedYear = '';
    }
    async loadContent() {
        const row1 = new ContentRow2(this._wrapper.getContentWrapper().getContent().getContentFluidElement());
        const card = new Card2({});
        card.appendTo(new ContentCol2(row1, ContentColSize2.col12).getElement());
        card.setTitle(new LangTextV2({ text: 'Ocean & Fishing' }));
        /*
         * Filter bar on top of the card body: year picker + map-size toggle.
         * Kept slim so the map still gets most of the vertical space.
         */
        const filterBar = jQuery('<div style="display:flex;align-items:center;gap:12px;margin-bottom:8px;"/>').appendTo(card.getBodyElement());
        jQuery('<label style="margin:0;font-weight:600;font-size:0.9rem;">Year</label>').appendTo(filterBar);
        const yearWrap = jQuery('<div style="min-width:200px;"/>').appendTo(filterBar);
        const yearSelect = new Select2({});
        yearSelect.appendTo(yearWrap);
        yearSelect.setValues([{ key: '', value: '— All sightings —' }]);
        yearSelect.setSelectedValue('');
        yearSelect.setChangeFn((value) => {
            this._selectedYear = value;
            void this._reload();
        });
        // Populate year list async — keep the UI usable in the meantime.
        SightingsApi.getYears().then((years) => {
            for (const y of years) {
                yearSelect.addValue({ key: `${y}`, value: `${y}` });
            }
        }).catch(() => null);
        // Map-size toggle: collapse the map down so the chart row fits on
        // the same screen, expand it back up for navigation.
        const mapToggleBtn = jQuery('<button type="button" class="btn btn-default btn-sm" title="Shrink / enlarge map" style="margin-left:auto;">'
            + '<i class="fa fa-compress-arrows-alt"/> <span class="map-toggle-label">Shrink map</span>'
            + '</button>').appendTo(filterBar);
        mapToggleBtn.on('click', () => {
            this._mapIsBig = !this._mapIsBig;
            this._applyMapHeight();
            mapToggleBtn.find('.fa').attr('class', this._mapIsBig ? 'fa fa-compress-arrows-alt' : 'fa fa-expand-arrows-alt');
            mapToggleBtn.find('.map-toggle-label').text(this._mapIsBig ? 'Shrink map' : 'Enlarge map');
        });
        /*
         * Two-column layout inside the card body: map on the left,
         * legend on the right. Done as raw flex because bambooo's
         * ContentCol API works at row scope only.
         */
        const flex = jQuery('<div style="display:flex;gap:12px;"/>').appendTo(card.getBodyElement());
        const mapWrap = jQuery('<div style="flex:1;min-width:0;"/>').appendTo(flex);
        const sideWrap = jQuery('<div style="width:280px;flex-shrink:0;"/>').appendTo(flex);
        this._map = new BaseMap(mapWrap);
        const wHeight = jQuery(window).height();
        if (wHeight) {
            // "Big": map gets almost the whole viewport — charts scroll below the fold.
            // "Small": chart grid fits on the same screen as the map.
            /*
             * navbar + filter bar. The AdminLTE 4 flip dropped the page-title
             * header (~60px) the old reserve also subtracted, so the map gets
             * that height back and reaches closer to the viewport foot.
             */
            const chromeReserve = 80 + 50;
            this._mapBigHeight = Math.max(420, wHeight - chromeReserve - 120);
            this._mapSmallHeight = Math.max(320, wHeight - chromeReserve - 760);
        }
        this._applyMapHeight();
        /*
         * Center on the user's organization when known; falls back to
         * BaseMap's default (Canary Islands) otherwise.
         */
        const currentuser = await UserAPI.getUserInfo();
        let initialCenter;
        if (currentuser === null || currentuser === void 0 ? void 0 : currentuser.organization) {
            initialCenter = [
                parseFloat(currentuser.organization.lon),
                parseFloat(currentuser.organization.lat)
            ];
        }
        this._map.load({
            initialCenterLonLat: initialCenter !== null && initialCenter !== void 0 ? initialCenter : [-17.3340221, 28.0525008],
            initialZoom: 8
        });
        /*
         * Delegated click handler for the "Regional currents (quiver)"
         * link rendered inside every map-feature popover. Bound once
         * on the page so popovers (which mount/unmount their own
         * tooltip nodes) don't need their own listeners.
         */
        jQuery(document).on('click.cmemsRegion', 'a.mwpa-cmems-region-link', (evt) => {
            var _a;
            evt.preventDefault();
            const sightingId = parseInt((_a = jQuery(evt.currentTarget).attr('data-sighting-id')) !== null && _a !== void 0 ? _a : '', 10);
            if (Number.isFinite(sightingId)) {
                CurrentRegionQuiverModal.show(sightingId, `Lat/Lon resolved from popover.`);
            }
        });
        // Base + overlay layers ----------------------------------------------------------------
        this._map.addLayer(new OsmBaseLayer());
        this._map.addLayer(new IdeeEsLayer());
        this._map.addLayer(new BathymetryLayer());
        this._map.addLayer(new OceanCurrentsLayer());
        /*
         * Metric layers — each registered separately so the
         * LayerSwitcher offers them as independent toggles. The user
         * picks one (or stacks several, they overlay). Adding a new
         * metric here only requires the layer class + an accessor
         * pulling the value off SightingEnvironmentEntry.
         */
        this._metricLayers = [
            { layer: new ChlorophyllMetricLayer(), accessor: (r) => r.chl_a_mg_m3_day },
            { layer: new FishingEffortMetricLayer(), accessor: (r) => r.fishing_hours_day_25km },
            { layer: new SeaSurfaceTemperatureMetricLayer(), accessor: (r) => r.sst_c_day },
            { layer: new AirTemperatureMetricLayer(), accessor: (r) => r.air_temperature_c_day },
            { layer: new WaveHeightMetricLayer(), accessor: (r) => r.wave_height_m_day }
        ];
        for (const { layer } of this._metricLayers) {
            this._map.addLayer(layer);
        }
        /*
         * Historical sightings — a standalone, muted-marker layer the user
         * opts into. Lazy-loaded on first show (and on year change while
         * shown) so the ~12 k historical points aren't fetched unless wanted.
         */
        this._oldLayer = new OldSightingPointsLayer();
        this._map.addLayer(this._oldLayer);
        this._oldLayerUnsub = this._oldLayer.onVisibleChange((visible) => {
            if (visible) {
                void this._reloadOld();
            }
        });
        // Recenter on org once the map is loaded.
        if (currentuser === null || currentuser === void 0 ? void 0 : currentuser.organization) {
            const center = fromLonLat([
                parseFloat(currentuser.organization.lon),
                parseFloat(currentuser.organization.lat)
            ]);
            this._map.setView(center, 8);
        }
        // Side panel: provenance legend -------------------------------------------------------
        this._legend = new ProvenanceLegend(sideWrap);
        this._legend.setStaticEntries([
            { label: Lang.i().l('OSM tiles'), source: 'OpenStreetMap (local cache)', url: 'https://www.openstreetmap.org/' },
            { label: Lang.i().l('Bathymetry'), source: 'EMODnet Bathymetry (local cache)', url: 'https://emodnet.ec.europa.eu/en/bathymetry' },
            { label: Lang.i().l('Relief (ES)'), source: 'IDEE / IGN España (local cache)', url: 'https://www.idee.es/' },
            { label: Lang.i().l('Weather + waves'), source: 'Open-Meteo marine + forecast API', url: 'https://open-meteo.com/' },
            { label: Lang.i().l('Sea depth'), source: 'EMODnet Bathymetry point lookup', url: 'https://emodnet.ec.europa.eu/en/bathymetry' },
            { label: Lang.i().l('Ocean currents'), source: 'CMEMS Global Analysis-Forecast 1/12° (WMTS)', url: 'https://data.marine.copernicus.eu/product/GLOBAL_ANALYSISFORECAST_PHY_001_024/description' }
        ]);
        this._legend.setMetricLayers(this._metricLayers.map(({ layer }) => layer));
        // Charts row (bottom of card body) ----------------------------------------------------
        const chartsRow = jQuery('<div style="margin-top:12px;"/>').appendTo(card.getBodyElement());
        this._charts = new MetricCharts(chartsRow);
        /*
         * Try to look up org for the additional org-context lookup so
         * the lazy import isn't dropped by the bundler (and so future
         * org-scope helpers have one in hand).
         */
        if (currentuser === null || currentuser === void 0 ? void 0 : currentuser.organization) {
            await OrganizationAPI.getOrganization(currentuser.organization.id).catch(() => null);
        }
        // Initial data load — no filter, default backend pagination.
        await this._reload();
    }
    /**
     * Push the currently selected height to the map element and trigger
     * an OL `updateSize` so tiles/layers re-fit immediately. No-op when
     * the map hasn't been instantiated yet.
     * @protected
     */
    _applyMapHeight() {
        if (!this._map) {
            return;
        }
        const height = this._mapIsBig ? this._mapBigHeight : this._mapSmallHeight;
        this._map.setHeight(height);
        this._map.updateSize();
    }
    /**
     * Fetch the environment list and feed every dependent widget.
     * Honours the year selector — empty year means "all sightings".
     * @protected
     */
    async _reload() {
        var _a;
        const filter = {
            limit: 5000,
            offset: 0
        };
        if (this._selectedYear !== '' && /^\d{4}$/.test(this._selectedYear)) {
            filter.period_from = `${this._selectedYear}-01-01`;
            filter.period_to = `${this._selectedYear}-12-31`;
        }
        const response = await SightingEnvironment.getList(filter);
        if (!response) {
            return;
        }
        const rows = response.list;
        for (const { layer, accessor } of this._metricLayers) {
            const points = rows.map((r) => {
                var _a;
                return ({
                    id: r.id,
                    lon: r.lon,
                    lat: r.lat,
                    value: (_a = accessor(r)) !== null && _a !== void 0 ? _a : null,
                    content: OceanFishingMap._buildTooltip(r)
                });
            });
            layer.setPoints(points);
            layer.refresh();
        }
        if (this._charts) {
            this._charts.setData(rows);
        }
        // Keep the historical layer in sync with the year filter, but only
        // when the user has it switched on (it's loaded lazily otherwise).
        if ((_a = this._oldLayer) === null || _a === void 0 ? void 0 : _a.isVisible()) {
            await this._reloadOld();
        }
    }
    /**
     * Fetch the historical (pre-app) sightings for the current year filter
     * and feed the old-sightings layer. Decoupled from {@link _reload}'s
     * metric pipeline — old rows carry no ocean/fishing metrics and must not
     * enter the metric overlays or the effort-sensitive charts.
     * @protected
     */
    async _reloadOld() {
        if (!this._oldLayer) {
            return;
        }
        const filter = {
            limit: 6000,
            offset: 0
        };
        if (this._selectedYear !== '' && /^\d{4}$/.test(this._selectedYear)) {
            filter.period_from = `${this._selectedYear}-01-01`;
            filter.period_to = `${this._selectedYear}-12-31`;
        }
        const response = await SightingEnvironment.getOldList(filter);
        if (!response) {
            return;
        }
        const points = response.list.map((r) => ({
            id: r.id,
            lon: r.lon,
            lat: r.lat,
            content: OceanFishingMap._buildOldTooltip(r)
        }));
        this._oldLayer.setPoints(points);
        this._oldLayer.refresh();
    }
    /**
     * Compact popover for a historical sighting: identity + whatever the
     * EnrichmentService could compute for the row (astronomy / weather).
     * No ocean/fishing block — old rows have none.
     * @protected
     */
    static _buildOldTooltip(r) {
        const fmt = (n, digits, suffix) => typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(digits)} ${suffix}` : '—';
        const rows = [
            '<b>Historical sighting</b> <span style="color:#777;">(pre-app)</span>',
            r.species_name ? `Species: ${r.species_name}` : null,
            r.date ? `Date: ${r.date}` : null,
            r.sun_elevation_deg === undefined && r.moon_phase === undefined ? null : '<i>— Sky &amp; Light —</i>',
            r.sun_elevation_deg === undefined ? null
                : `Sun elevation: ${fmt(r.sun_elevation_deg, 1, '°')}${typeof r.sun_azimuth_deg === 'number' ? ` @ ${Math.round(r.sun_azimuth_deg)}°` : ''}`,
            r.day_length_h === undefined ? null : `Day length: ${fmt(r.day_length_h, 1, 'h')}`,
            r.moon_phase === undefined ? null
                : `Moon: ${fmt(r.moon_illumination_pct, 0, '%')} lit (phase ${fmt(r.moon_phase, 2, '')})`,
            r.pressure_msl_hpa_day === undefined && r.cloud_cover_pct_day === undefined ? null : '<i>— Weather —</i>',
            r.pressure_msl_hpa_day === undefined ? null : `Pressure: ${fmt(r.pressure_msl_hpa_day, 0, 'hPa')}`,
            r.cloud_cover_pct_day === undefined ? null : `Cloud cover: ${fmt(r.cloud_cover_pct_day, 0, '%')}`,
            r.precipitation_mm_day === undefined ? null : `Precipitation: ${fmt(r.precipitation_mm_day, 1, 'mm')}`,
            r.upwelling_index_day === undefined && r.seabed_slope_deg === undefined
                && r.sst_front_grad_c_km_day === undefined && r.chl_front_grad_mg_m3_km_day === undefined ? null : '<i>— Derived —</i>',
            r.upwelling_index_day === undefined ? null
                : `Upwelling index: ${fmt(r.upwelling_index_day, 1, 'm³/s per 100 m')}`,
            r.seabed_slope_deg === undefined ? null : `Seabed slope: ${fmt(r.seabed_slope_deg, 1, '°')}`,
            r.shelf_distance_km === undefined ? null : `Shelf-break dist: ${fmt(r.shelf_distance_km, 1, 'km')}`,
            r.sst_front_grad_c_km_day === undefined ? null
                : `SST front: ${fmt(r.sst_front_grad_c_km_day, 3, '°C/km')}`,
            r.chl_front_grad_mg_m3_km_day === undefined ? null
                : `Chl front: ${fmt(r.chl_front_grad_mg_m3_km_day, 4, 'mg/m³/km')}`,
            r.oxygen_mmol_m3_day === undefined && r.ph_day === undefined
                && r.nitrate_mmol_m3_day === undefined && r.mld_m_day === undefined ? null : '<i>— Biogeochemistry —</i>',
            r.oxygen_mmol_m3_day === undefined ? null : `Oxygen: ${fmt(r.oxygen_mmol_m3_day, 1, 'mmol/m³')}`,
            r.ph_day === undefined ? null : `pH: ${fmt(r.ph_day, 2, '')}`,
            r.nitrate_mmol_m3_day === undefined ? null : `Nitrate: ${fmt(r.nitrate_mmol_m3_day, 2, 'mmol/m³')}`,
            r.mld_m_day === undefined ? null : `Mixed-layer depth: ${fmt(r.mld_m_day, 1, 'm')}`
        ];
        return rows.filter((row) => row !== null).join('<br>');
    }
    /**
     * @protected
     */
    static _buildTooltip(r) {
        const fmt = (n, digits, suffix) => typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(digits)} ${suffix}` : '—';
        const rows = [
            `<b>Sighting #${r.id}</b>`,
            r.species_name ? `Species: ${r.species_name}` : null,
            r.date ? `Date: ${r.date}` : null,
            '<i>— Ocean —</i>',
            `Chl-a: ${fmt(r.chl_a_mg_m3_day, 2, 'mg/m³')}`,
            `Salinity: ${fmt(r.salinity_psu_day, 2, 'PSU')}`,
            `SST: ${fmt(r.sst_c_day, 1, '°C')}`,
            `Current: ${fmt(r.current_speed_m_s_day, 2, 'm/s')}`,
            r.current_region_mean_speed_m_s_day === undefined ? null
                : `Region mean / max: ${fmt(r.current_region_mean_speed_m_s_day, 2, '')} / ${fmt(r.current_region_max_speed_m_s_day, 2, 'm/s')}`,
            r.current_curl_s_inv_day === undefined ? null
                : `Vorticity (curl): ${(r.current_curl_s_inv_day * 1e5).toFixed(2)} ×10⁻⁵/s`,
            r.current_divergence_s_inv_day === undefined ? null
                : `Divergence: ${(r.current_divergence_s_inv_day * 1e5).toFixed(2)} ×10⁻⁵/s`,
            r.current_coastal_component_m_s_day === undefined ? null
                : `Coastal (cross-shore): ${fmt(r.current_coastal_component_m_s_day, 2, 'm/s')}${typeof r.current_coastal_component_m_s_day === 'number' ? ` (${r.current_coastal_component_m_s_day >= 0 ? 'offshore' : 'onshore'})` : ''}`,
            `<a href="#" class="mwpa-cmems-region-link" data-sighting-id="${r.id}">→ Regional currents (quiver)</a>`,
            `Depth: ${fmt(r.depth_m, 0, 'm')}`,
            r.coordinate_uncertainty_m === undefined ? null : `Coord. uncertainty: ${fmt(r.coordinate_uncertainty_m, 0, 'm')}`,
            '<i>— Weather —</i>',
            `Air temp: ${fmt(r.air_temperature_c_day, 1, '°C')}`,
            `Wave height: ${fmt(r.wave_height_m_day, 2, 'm')}`,
            `Wave period: ${fmt(r.wave_period_s_day, 1, 's')}`,
            r.uv_index_day === undefined ? null : `UV index: ${fmt(r.uv_index_day, 1, '')}`,
            r.wind_speed_m_s_day === undefined ? null
                : `Wind: ${fmt(r.wind_speed_m_s_day, 1, 'm/s')}${typeof r.wind_direction_deg_day === 'number' ? ` @ ${Math.round(r.wind_direction_deg_day)}°` : ''}${typeof r.wind_gusts_m_s_day === 'number' ? ` (gust ${fmt(r.wind_gusts_m_s_day, 1, 'm/s')})` : ''}`,
            r.pressure_msl_hpa_day === undefined ? null : `Pressure: ${fmt(r.pressure_msl_hpa_day, 0, 'hPa')}`,
            r.cloud_cover_pct_day === undefined ? null : `Cloud cover: ${fmt(r.cloud_cover_pct_day, 0, '%')}`,
            r.visibility_m_day === undefined ? null : `Visibility: ${fmt(r.visibility_m_day / 1000, 1, 'km')}`,
            r.precipitation_mm_day === undefined ? null : `Precipitation: ${fmt(r.precipitation_mm_day, 1, 'mm')}`,
            '<i>— Sky &amp; Light —</i>',
            r.sun_elevation_deg === undefined ? null
                : `Sun elevation: ${fmt(r.sun_elevation_deg, 1, '°')}${typeof r.sun_azimuth_deg === 'number' ? ` @ ${Math.round(r.sun_azimuth_deg)}°` : ''}`,
            r.day_length_h === undefined ? null : `Day length: ${fmt(r.day_length_h, 1, 'h')}`,
            r.moon_phase === undefined ? null
                : `Moon: ${fmt(r.moon_illumination_pct, 0, '%')} lit (phase ${fmt(r.moon_phase, 2, '')})`,
            '<i>— Derived —</i>',
            r.upwelling_index_day === undefined ? null
                : `Upwelling index: ${fmt(r.upwelling_index_day, 1, 'm³/s per 100 m')}`,
            r.seabed_slope_deg === undefined ? null : `Seabed slope: ${fmt(r.seabed_slope_deg, 1, '°')}`,
            r.shelf_distance_km === undefined ? null : `Shelf-break dist: ${fmt(r.shelf_distance_km, 1, 'km')}`,
            r.sst_front_grad_c_km_day === undefined ? null
                : `SST front: ${fmt(r.sst_front_grad_c_km_day, 3, '°C/km')}`,
            r.chl_front_grad_mg_m3_km_day === undefined ? null
                : `Chl front: ${fmt(r.chl_front_grad_mg_m3_km_day, 4, 'mg/m³/km')}`,
            r.oxygen_mmol_m3_day === undefined && r.ph_day === undefined
                && r.nitrate_mmol_m3_day === undefined && r.mld_m_day === undefined ? null : '<i>— Biogeochemistry —</i>',
            r.oxygen_mmol_m3_day === undefined ? null : `Oxygen: ${fmt(r.oxygen_mmol_m3_day, 1, 'mmol/m³')}`,
            r.ph_day === undefined ? null : `pH: ${fmt(r.ph_day, 2, '')}`,
            r.nitrate_mmol_m3_day === undefined ? null : `Nitrate: ${fmt(r.nitrate_mmol_m3_day, 2, 'mmol/m³')}`,
            r.mld_m_day === undefined ? null : `Mixed-layer depth: ${fmt(r.mld_m_day, 1, 'm')}`,
            '<i>— Fishing —</i>',
            `Fishing (25 km): ${fmt(r.fishing_hours_day_25km, 1, 'h/day')}`,
            `Vessels (25 km): ${typeof r.vessel_count_day_25km === 'number' ? r.vessel_count_day_25km : '—'}`,
            r.top_gear_type ? `Top gear: ${r.top_gear_type}` : null,
            r.top_flag ? `Top flag: ${r.top_flag}` : null
        ];
        return rows.filter((row) => row !== null).join('<br>');
    }
}
